import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon,
  CurrencyDollarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import { bulkOrderService, BulkOrder, BulkOrderStats } from '../../services/bulkOrderService';
import { NETWORKS } from '../../utils/constants';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProgressBar from '../../components/common/ProgressBar';

const BulkOrderHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [bulkOrders, setBulkOrders] = useState<BulkOrder[]>([]);
  const [stats, setStats] = useState<BulkOrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    network: '',
    timeframe: '30d'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);
  filtersRef.current = filters;
  paginationRef.current = pagination;

  const loadBulkOrders = useCallback(async () => {
    const currentFilters = filtersRef.current;
    const currentPagination = paginationRef.current;

    try {
      setLoading(true);
      const response = await bulkOrderService.getBulkOrders({
        page: currentPagination.page,
        limit: currentPagination.limit,
        ...(currentFilters.status && { status: currentFilters.status }),
        ...(currentFilters.network && { network: currentFilters.network })
      });
      
      setBulkOrders(response.bulkOrders);
      setPagination(response.pagination);
    } catch (error: any) {

      toast.error(error.response?.data?.message || 'Failed to load bulk orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await bulkOrderService.getBulkOrderStats(filtersRef.current.timeframe as any);
      setStats(response.stats);
    } catch (error) {

    }
  }, []);

  // Load bulk orders and stats
  useEffect(() => {
    loadBulkOrders();
    loadStats();
  }, [filters, loadBulkOrders, loadStats]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const exportBulkOrder = (bulkOrder: BulkOrder) => {
    const csvContent = bulkOrderService.exportToCSV(bulkOrder);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-order-${bulkOrder.orderNumber}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Order exported to CSV');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
      case 'partially_completed':
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'draft':
      case 'pending_payment':
      case 'pending_admin_processing':
      case 'processing':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNetworkColor = (network: string) => {
    switch (network) {
      case 'mtn':
        return NETWORKS.MTN.color;
      case 'airteltigo':
        return NETWORKS.AIRTELTIGO.color;
      case 'telecel':
        return NETWORKS.TELECEL.color;
      default:
        return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Bulk Order History
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
                View and manage your bulk orders
              </p>
            </div>
            <div className="flex items-center">
              <Button
                variant="primary"
                onClick={() => navigate('/bulk-orders')}
              >
                <UserGroupIcon className="h-5 w-5 mr-2" />
                New Bulk Order
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Statistics */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 mr-4">
                  <UserGroupIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Total Orders
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.totalOrders || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 mr-4">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Completed Orders
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.completedOrders || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900 mr-4">
                  <UserGroupIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Total Recipients
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.totalRecipients || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 mr-4">
                  <CurrencyDollarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Total Value
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    GHS {stats?.totalValue?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Filters
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ status: '', network: '', timeframe: '30d' })}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="partially_completed">Partially Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Network
                </label>
                <select
                  value={filters.network}
                  onChange={(e) => handleFilterChange('network', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Networks</option>
                  <option value="mtn">MTN</option>
                  <option value="airteltigo">AirtelTigo</option>
                  <option value="telecel">Telecel</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timeframe
                </label>
                <select
                  value={filters.timeframe}
                  onChange={(e) => handleFilterChange('timeframe', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Bulk Orders List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Bulk Orders ({pagination.total})
              </h2>
            </div>

            {bulkOrders.length === 0 ? (
              <div className="text-center py-12">
                <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No bulk orders found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {filters.status || filters.network 
                    ? 'Try adjusting your filters'
                    : 'Create your first bulk order to get started'
                  }
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/bulk-orders')}
                >
                  Create Bulk Order
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bulkOrders.map((order) => {
                  const statusInfo = bulkOrderService.formatStatus(order.status);
                  const progressInfo = bulkOrderService.getStatusProgress(order);
                  
                  return (
                    <div
                      key={order._id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
                          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gray-100 dark:bg-gray-800">
                            {getStatusIcon(order.status)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white break-words">
                              {order.orderNumber}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge variant={statusInfo.variant as any} className="text-xs">
                                {statusInfo.text}
                              </Badge>
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getNetworkColor(order.network) }}
                              />
                              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {order.network.toUpperCase()}
                              </span>
                              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                            GHS {order.pricing?.totalPrice?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            {order.totalRecipients || 0} recipients
                          </p>
                        </div>
                      </div>

                      {order.status === 'processing' && (
                        <div className="mt-4">
                          <ProgressBar
                            value={progressInfo.percentage}
                            className="mb-2"
                          />
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {progressInfo.successful} successful, {progressInfo.failed} failed
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {order.dataPackage?.name || 'Multiple Packages'}
                          </span>
                          {' • '}
                          {order.dataPackage?.size || 'Various Sizes'}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportBulkOrder(order)}
                            className="w-full sm:w-auto"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Export</span>
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/bulk-orders/${order._id}`)}
                            className="w-full sm:w-auto"
                          >
                            <EyeIcon className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">View Details</span>
                            <span className="sm:hidden">View</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400 break-words">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default BulkOrderHistoryPage;
